/**
 * Cross-agency fire dedupe.
 *
 * The same active fire can be reported by WildCAD-E (dispatch), NIFC WFIGS
 * (federal) and WA DNR (state). Per user preference: when a WildCAD incident
 * and a DNR fire describe the SAME fire (same normalized name within ~5 mi,
 * or reported locations within ~0.5 mi), the app shows the DNR fire and
 * hides the WildCAD entry. DNR fires are still deduped against NIFC.
 */

// 0.5 mi ≈ 0.0072° lat. At ~48°N, 1° lon = cos(48°)·69 ≈ 46 mi, so
// 0.5 mi ≈ 0.0109° lon. Use a slightly generous square box.
const LAT_TOL = 0.008;
const LON_TOL = 0.012;
// Same-name match allows a wider box (~5 mi) — coordinates from different
// agencies can differ by a few miles for the same fire.
const NAME_LAT_TOL = 0.08;
const NAME_LON_TOL = 0.12;

function near(a, b, latTol = LAT_TOL, lonTol = LON_TOL) {
  return (
    Math.abs(a.latitude - b.latitude) <= latTol &&
    Math.abs(a.longitude - b.longitude) <= lonTol
  );
}

// WildCAD prefixes incident names with dispatch codes ("SA - Plum") while
// NIFC publishes the bare name ("PLUM") — strip those before comparing.
const DISPATCH_PREFIX = /^(sa|sl|rx|ia|wf|fa|complex)\s+/;

export function normName(n) {
  return (n || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(DISPATCH_PREFIX, "");
}

export function sameFire(inc, dnr) {
  if (inc.latitude == null || dnr.latitude == null) return false;
  if (near(inc, dnr)) return true;
  const n1 = normName(inc.name);
  const n2 = normName(dnr.name);
  if (n1 && n2 && n1 === n2 && near(inc, dnr, NAME_LAT_TOL, NAME_LON_TOL)) {
    return true;
  }
  return false;
}

/** Hide WildCAD incidents that duplicate a DNR fire — DNR wins. */
export function dedupeWildcadAgainstDnr(incidents, dnrFires) {
  if (!incidents?.length || !dnrFires?.length) return incidents || [];
  return incidents.filter((inc) => !dnrFires.some((d) => sameFire(inc, d)));
}

/** Best WildCAD record for a DNR/NIFC fire when several match (e.g. a stale
 *  "Miscellaneous" duplicate next to the real Wildfire record). */
export function bestWildcadMatch(incidents, ref) {
  const nRef = normName(ref?.name);
  const matches = (incidents || []).filter((i) => {
    if (i.latitude != null && ref?.latitude != null) {
      if (sameFire(i, ref)) return true;
      // Same name a few miles apart — stale dispatch coords drift (~10 mi box).
      return !!nRef && normName(i.name) === nRef && near(i, ref, 0.16, 0.24);
    }
    // Records missing coordinates (stale dispatch entries) match on name.
    return !!nRef && normName(i.name) === nRef;
  });
  if (matches.length <= 1) return matches[0] || null;
  const score = (i) => {
    let s = 0;
    if ((i.type || "").toLowerCase() === "wildfire") s += 100;
    if ((i.status_label || "") === "ACTIVE") s += 50;
    if (i.acres != null) s += 25;
    return s;
  };
  return matches.sort((a, b) => score(b) - score(a) || (b.date || "").localeCompare(a.date || ""))[0];
}

/** Find the NIFC fire perimeter matching an incident (name + rough proximity —
 *  big-fire centroids drift miles from the dispatch point). */
export function matchPerimeter(incident, perimeters) {
  if (incident?.latitude == null || !perimeters?.length) return null;
  const n1 = normName(incident.name);
  if (!n1) return null;
  return (
    perimeters.find((p) => {
      const lat = p.centroid_lat ?? p.latitude;
      const lon = p.centroid_lon ?? p.longitude;
      if (lat == null || lon == null) return false;
      return (
        normName(p.name) === n1 &&
        Math.abs(lat - incident.latitude) <= 0.25 &&
        Math.abs(lon - incident.longitude) <= 0.35
      );
    }) || null
  );
}

/** Best-known acreage across sources: mapped GIS perimeter > NIFC point >
 *  WildCAD dispatch. Returns { acres, source } or null. */
export function bestFireAcres(incident, nifcMatch, perimeterMatch) {
  const wc = parseFloat(incident?.acres);
  const candidates = [
    { acres: Number.isNaN(wc) ? null : wc, source: "wildcad" },
    { acres: nifcMatch?.incident_size ?? null, source: "nifc" },
    { acres: perimeterMatch?.acres ?? null, source: "mapped" },
  ].filter((c) => c.acres != null);
  if (!candidates.length) return null;
  return candidates.reduce((a, b) => (b.acres > a.acres ? b : a));
}
